import type { User } from "@atlas/shared";

// The one-off "get the app" step after signing up on a phone (WelcomeAppPage).
// Lives outside the page so the router's dashboard beforeLoad can ask
// shouldOfferApp() without pulling the page into the main bundle.
//
// "Once" = a per-user localStorage flag, set the moment the page shows, plus
// a 24-hour window on the account's age so an old account on a new phone is
// never sent here.

const SEEN_PREFIX = "atlas-welcome-app-seen:";
const NEW_ACCOUNT_MS = 24 * 60 * 60 * 1000;

export const APP_STORE_URL = "https://apps.apple.com/gb/app/atlas-hiking-record/id6788635039";
export const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=co.uk.goatlas.app&hl=en_GB";

export type Phone = "ios" | "android";

export function detectPhone(): Phone | null {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent;
  if (/iPhone|iPod/i.test(ua)) return "ios";
  // iPadOS reports itself as a Mac; touch points give it away.
  if (/iPad/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return null;
}

function hasSeen(userId: string): boolean {
  try {
    return localStorage.getItem(SEEN_PREFIX + userId) !== null;
  } catch {
    // Storage blocked: never risk showing it on every visit.
    return true;
  }
}

export function markSeen(userId: string): void {
  try {
    localStorage.setItem(SEEN_PREFIX + userId, "1");
  } catch {
    // best-effort
  }
}

/** True for a brand-new account on a phone that hasn't seen the page. Read by the dashboard route. */
export function shouldOfferApp(user: Pick<User, "id" | "createdAt"> | null | undefined): boolean {
  if (!user || detectPhone() == null) return false;
  const age = Date.now() - new Date(user.createdAt).getTime();
  if (!(age >= 0 && age < NEW_ACCOUNT_MS)) return false;
  return !hasSeen(user.id);
}
